if (!customElements.get('design-box-variant-picker')) {
  const formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const formatString =
      format || window.KROWN?.settings?.shop_money_format || window.Shopify?.money_format || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return String(cents / 100);

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      const thousandsSep = thousands || ',';
      const decimalSep = decimal || '.';
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousandsSep}`);
      const centsAmount = parts[1] ? decimalSep + parts[1] : '';
      return dollarsAmount + centsAmount;
    };

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    let formatted = formatString.replace(placeholderRegex, value);
    const isoCode = window.KROWN?.settings?.iso_code;
    if (isoCode) formatted += isoCode;
    return formatted;
  };

  const generateBundalId = () => {
    const length = Math.floor(Math.random() * 3) + 6;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';

    for (let i = 0; i < length; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  };

  customElements.define(
    'design-box-variant-picker',
    class DesignBoxVariantPicker extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.rowCount = Number(this.dataset.rowCount || 0);
        this.addToCartText = this.dataset.addToCartText || 'Add to cart';
        this.completeSelectionText = this.dataset.completeSelectionText || 'Complete Your Selection';
        this.productPage =
          this.closest('[data-js-product-component]') || document.querySelector(`#product-${this.sectionId}`);
        this._prefix = window.KT_PREFIX || '';
        this._submitHandler = this.onSubmitHandler.bind(this);
        this._formSubmitBound = false;

        this.bindEvents();
        this.initSmoothAccordions();
        this.scheduleDomSetup();
      }

      scheduleDomSetup() {
        const setup = () => {
          this.cacheDomRefs();
          this.updateRowTitles();
          this.updateTotalPrice();
          this.updateSubmitState();
        };

        setup();
        requestAnimationFrame(setup);
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', setup, { once: true });
        }
      }

      cacheDomRefs() {
        if (!this.productForm) {
          const formEl = document.querySelector(`#product-form-${this.sectionId}`);
          this.productForm = formEl?.closest('product-form');
          if (this.productForm && !this._formSubmitBound) {
            this.productForm.addEventListener('submit', this._submitHandler, true);
            this._formSubmitBound = true;
          }
        }

        this.submitButton =
          this.productForm?.querySelector('[data-js-product-add-to-cart]') ||
          this.productForm?.querySelector('[type="submit"]');
        this.submitButtonLabel = this.productForm?.querySelector('[data-js-product-add-to-cart-text]');
        this.variantInput = this.productForm?.querySelector('input[name="id"]');
      }

      bindEvents() {
        this.bindVariantDeselect();

        this.addEventListener('change', (event) => {
          if (!event.target.matches('.design-box-variant-item__input')) return;
          this.cacheDomRefs();
          this.updateRowTitles();
          this.updateTotalPrice();
          this.updateSubmitState();
          this.updateHiddenVariantInput();
        });

        this.addEventListener(
          'input',
          (event) => {
            if (!event.target.matches('.design-box-variant-item__input')) return;
            this.cacheDomRefs();
            this.updateRowTitles();
            this.updateTotalPrice();
            this.updateSubmitState();
            this.updateHiddenVariantInput();
          },
          true
        );
      }

      bindVariantDeselect() {
        const getInputFromEvent = (event) => {
          const input = event.target.closest('.design-box-variant-item__input');
          if (input) return input;

          const label = event.target.closest('.design-box-variant-item__label[for]');
          if (!label) return null;

          return document.getElementById(label.getAttribute('for'));
        };

        this.addEventListener(
          'pointerdown',
          (event) => {
            const input = getInputFromEvent(event);
            if (input?.type === 'radio') {
              input._wasChecked = input.checked;
            }
          },
          true
        );

        this.addEventListener(
          'click',
          (event) => {
            const input = getInputFromEvent(event);
            if (!input || input.type !== 'radio') return;

            if (input._wasChecked) {
              event.preventDefault();
              event.stopPropagation();
              input.checked = false;
              input._wasChecked = false;
              this.cacheDomRefs();
              this.updateRowTitles();
              this.updateTotalPrice();
              this.updateSubmitState();
              this.updateHiddenVariantInput();
            }
          },
          true
        );
      }

      initSmoothAccordions() {
        this.querySelectorAll('.design-box-row__accordion').forEach((details) => {
          this.setupSmoothAccordion(details, {
            groupSelector: '.design-box-row__accordion',
          });
        });

        this.querySelectorAll('.design-box-row').forEach((row) => {
          row.querySelectorAll('.design-box-inner-accordion').forEach((details) => {
            this.setupSmoothAccordion(details, {
              group: row.querySelectorAll('.design-box-inner-accordion'),
            });
          });
        });
      }

      setupSmoothAccordion(details, { groupSelector, group }) {
        const summary = details.querySelector(':scope > summary');
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!summary || !body) return;

        if (details.open) {
          body.style.gridTemplateRows = '1fr';
        }

        summary.addEventListener('click', (event) => {
          event.preventDefault();

          const willOpen = !details.open;

          if (willOpen) {
            const siblings = group || this.querySelectorAll(groupSelector);
            siblings?.forEach((other) => {
              if (other !== details && other.open) {
                this.closeSmoothAccordion(other);
              }
            });
            this.openSmoothAccordion(details);
          } else {
            this.closeSmoothAccordion(details);
          }
        });
      }

      openSmoothAccordion(details) {
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!body) {
          details.open = true;
          return;
        }

        details.open = true;
        body.style.gridTemplateRows = '0fr';
        requestAnimationFrame(() => {
          body.style.gridTemplateRows = '1fr';
        });
      }

      closeSmoothAccordion(details) {
        const body = details.querySelector(':scope > .design-box-accordion__body');
        if (!body) {
          details.open = false;
          return;
        }

        body.style.gridTemplateRows = '0fr';

        const onTransitionEnd = (event) => {
          if (event.target !== body) return;
          details.open = false;
          body.style.gridTemplateRows = '';
          body.removeEventListener('transitionend', onTransitionEnd);
        };

        body.addEventListener('transitionend', onTransitionEnd);
      }

      getRows() {
        return Array.from(this.querySelectorAll('.design-box-row'));
      }

      getRowSelection(row) {
        return row.querySelector('.design-box-variant-item__input:checked');
      }

      getAllSelections() {
        return this.getRows()
          .map((row) => {
            const selected = this.getRowSelection(row);
            if (!selected) return null;

            const rowTitle = row.querySelector('[data-row-title]')?.dataset.rowTitle || '';
            return {
              variantId: selected.dataset.variantId,
              variantTitle: selected.dataset.variantTitle,
              price: Number(selected.dataset.variantPrice || 0),
              available: selected.dataset.variantAvailable === 'true',
              rowTitle,
              rowIndex: row.dataset.rowIndex,
            };
          })
          .filter(Boolean);
      }

      updateRowTitles() {
        this.getRows().forEach((row) => {
          const titleEl = row.querySelector('[data-row-title]');
          if (!titleEl) return;

          const selectedCount = this.getRowSelection(row) ? 1 : 0;
          let countEl = titleEl.querySelector('.design-box-row__title-count');
          if (!countEl) {
            countEl = document.createElement('span');
            countEl.className = 'design-box-row__title-count';
            titleEl.appendChild(countEl);
          }
          countEl.textContent = ` (${selectedCount}/1)`;
        });
      }

      getPriceElements() {
        const root = this.productPage;
        if (!root) return [];

        return Array.from(
          root.querySelectorAll(
            '[data-update-block="price-compact"] .product-price--original, [data-update-block="price-extended"] .product-price--original'
          )
        );
      }

      updateTotalPrice() {
        const selections = this.getAllSelections();
        const total =
          selections.length === 0
            ? 0
            : selections.reduce((sum, selection) => sum + selection.price, 0);
        const formatted = formatMoney(total);

        this.getPriceElements().forEach((el) => {
          el.textContent = formatted;
        });

        const root = this.productPage;
        if (root) {
          root.querySelectorAll('.product-price--compare, .product-pdp__save-badge').forEach((el) => {
            el.classList.add('hidden');
          });
        }
      }

      setSubmitButtonLabel(text) {
        if (!this.submitButtonLabel) return;

        const icon = this.submitButtonLabel.querySelector('[aria-hidden="true"]');
        if (icon) {
          this.submitButtonLabel.textContent = '';
          this.submitButtonLabel.appendChild(icon);
          this.submitButtonLabel.append(document.createTextNode(text));
        } else {
          this.submitButtonLabel.textContent = text;
        }
      }

      updateSubmitState() {
        this.cacheDomRefs();
        if (!this.submitButton) return;

        const selections = this.getAllSelections();
        const allRowsSelected = selections.length === this.rowCount;
        const allAvailable = selections.every((selection) => selection.available);
        const canSubmit = allRowsSelected && allAvailable;

        this.submitButton.toggleAttribute('disabled', !canSubmit);
        this.submitButton.classList.toggle('disabled', !canSubmit);
        this.submitButton.setAttribute('aria-disabled', canSubmit ? 'false' : 'true');

        if (canSubmit) {
          this.setSubmitButtonLabel(this.addToCartText);
        } else {
          this.setSubmitButtonLabel(this.completeSelectionText);
        }
      }

      updateHiddenVariantInput() {
        this.cacheDomRefs();
        const selections = this.getAllSelections();
        if (!this.variantInput) return;

        if (selections.length === 0) {
          this.variantInput.value = '';
          this.variantInput.disabled = true;
          return;
        }

        this.variantInput.value = selections[0].variantId;
        this.variantInput.disabled = false;
      }

      showFormError(message) {
        if (!this.productForm) return;

        const existing = document.getElementById('product-page-form-error-cart-alert');
        existing?.remove();

        const alert = document.createElement('span');
        alert.className = `${this._prefix}alert ${this._prefix}alert--error`;
        alert.id = 'product-page-form-error-cart-alert';
        alert.style.marginTop = '1em';
        alert.style.marginBottom = '0';
        alert.textContent = message;
        this.productForm.append(alert);
      }

      async refreshCartAfterAdd() {
        const cartType = this.productForm?.hasAttribute('data-ajax-cart')
          ? window.KROWN?.settings?.cart_action === 'popup'
            ? 'popup'
            : 'ajax'
          : 'page';

        if (cartType === 'page') {
          window.location.href = window.KROWN?.settings?.routes?.cart_url || '/cart';
          return;
        }

        let text;
        if (cartType === 'popup' && this.productForm?.dataset.productUrl) {
          const variantId = this.variantInput?.value || '';
          text = await fetch(
            `${this.productForm.dataset.productUrl}?variant=${variantId}&section_id=helper-cart-popup`
          ).then((response) => response.text());
          const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');
          const popupContent = document.querySelector('[data-js-site-cart-popup-content]');
          if (popupContent) {
            popupContent.innerHTML = sectionInnerHTML.querySelector('.shopify-section')?.innerHTML || '';
          }
        } else {
          text = await fetch('?section_id=helper-cart').then((response) => response.text());
          if (typeof window.renderHelperCart === 'function') {
            window.renderHelperCart(text, { defaultShipping: true });
          }
        }
        const cartRecommendations =
          document.getElementById('cart-recommendations') || document.querySelector('cart-recommendations');
        if (cartRecommendations?.generateRecommendations) {
          cartRecommendations.innerHTML = '';
          cartRecommendations.generateRecommendations();
        }

        if (window.KROWN?.functions?.dispatchStandardEvent) {
          window.KROWN.functions.dispatchStandardEvent('CartLinesUpdateEvent', this.productForm, {
            action: 'add',
            context: 'product-design-box',
            lines: [],
            promise: Promise.resolve({ cart: null }),
          });
        }

        const drawer = document.getElementById('site-cart-sidebar');
        if (drawer && typeof drawer.show === 'function') {
          drawer.show();
        }

        if (document.getElementById('screen-reader-info') && window.KROWN?.settings?.locales?.cart_announcement) {
          document.getElementById('screen-reader-info').innerText = window.KROWN.settings.locales.cart_announcement;
          setTimeout(() => {
            document.getElementById('screen-reader-info').innerText = '';
          }, 1000);
        }
      }

      async onSubmitHandler(event) {
        const form = event.target.closest?.(`#product-form-${this.sectionId}`);
        if (!form) return;

        this.cacheDomRefs();
        if (!this.productForm?.contains(form)) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const selections = this.getAllSelections().sort(
          (a, b) => Number(a.rowIndex) - Number(b.rowIndex)
        );
        if (selections.length !== this.rowCount) return;

        const submitButton = this.submitButton;
        submitButton?.setAttribute('aria-disabled', 'true');
        submitButton?.classList.add(`${this._prefix}working`);

        const bundalId = generateBundalId();
        const sourceProps = window.KROWN?.functions?.getCartLineItemSourceProperties?.(this) || {
          _source: 'pdp',
        };
        const items = selections.map((selection) => ({
          id: Number(selection.variantId),
          quantity: 1,
          properties: {
            _bundal: bundalId,
            ...sourceProps,
            [selection.rowTitle]: selection.variantTitle,
          },
        }));

        const cartAddUrl = `${window.KROWN?.settings?.routes?.cart_add_url || '/cart/add'}.js`;

        try {
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({ items }),
          });

          const result = await response.json().catch(() => ({}));

          if (!response.ok || result.status) {
            const message = result.description || result.message || 'Could not add items to cart.';
            this.showFormError(message);
            return;
          }

          await this.refreshCartAfterAdd();
        } catch (error) {
          console.error(error);
          this.showFormError(error.message || 'Could not add items to cart.');
        } finally {
          submitButton?.classList.remove(`${this._prefix}working`);
          this.updateSubmitState();
        }
      }
    }
  );
}
